import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import i18n from "src/i18n";

export interface DataMgmtSearchFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Optional min width for the field (default matches document categories). */
  minWidth?: number;
  maxWidth?: number;
}

/**
 * Catalog-style search: magnifier inside the input (same pattern as Document Categories).
 */
export const DataMgmtSearchField: React.FC<DataMgmtSearchFieldProps> = ({
  id,
  value,
  onChange,
  placeholder,
  minWidth = 160,
  maxWidth = 480,
}) => {
  const rtlList = i18n.language === "ar";

  return (
    <div
      className="services-catalog-search-wrap position-relative flex-grow-1 doc-categories-search-wrap"
      style={{ minWidth, maxWidth }}
    >
      <HiMagnifyingGlass
        className="position-absolute text-muted"
        size={16}
        style={{
          ...(rtlList ? { right: 10, left: "auto" } : { left: 10, right: "auto" }),
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
        aria-hidden
      />
      <input
        id={id}
        type="search"
        className="form-control form-control-sm border bg-white services-catalog-search-input"
        style={{
          height: 32,
          borderRadius: 8,
          ...(rtlList ? { paddingRight: 34, paddingLeft: 12 } : { paddingLeft: 34, paddingRight: 12 }),
        }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={placeholder}
        dir={rtlList ? "rtl" : "ltr"}
      />
    </div>
  );
};

export interface DataMgmtCatalogPaginationProps {
  /** Current page (1-based). */
  page: number;
  totalPages: number;
  /** Total items (used to disable nav when empty). */
  totalFiltered: number;
  busy?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * Document-categories style pagination: Previous / numbered pages / Next + meta line.
 */
export const DataMgmtCatalogPagination: React.FC<DataMgmtCatalogPaginationProps> = ({
  page,
  totalPages,
  totalFiltered,
  busy = false,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const hasCatalogPages = totalFiltered > 0;

  const visiblePaginationPages = useMemo((): (number | "ellipsis")[] => {
    const total = Math.max(0, totalPages);
    if (total <= 0) return [];
    const cur = Math.min(Math.max(1, page), total);
    const delta = 2;
    const range: number[] = [];
    for (let i = 1; i <= total; i += 1) {
      if (i === 1 || i === total || (i >= cur - delta && i <= cur + delta)) {
        range.push(i);
      }
    }
    const result: (number | "ellipsis")[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined && i - prev > 1) {
        result.push("ellipsis");
      }
      result.push(i);
      prev = i;
    }
    return result;
  }, [page, totalPages]);

  return (
    <div className="doc-categories-pagination-wrap data-mgmt-pagination-bar">
      <nav
        className="doc-categories-pagination-nav d-flex flex-wrap align-items-center justify-content-center gap-1"
        aria-label={String(
          t("document_categories.pageIndicator", {
            page,
            totalPages,
            defaultValue: `Page ${page} / ${totalPages}`,
          }),
        )}
      >
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page <= 1 || busy}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <MdChevronLeft size={18} aria-hidden />
          <span>{String(t("common.previous", { defaultValue: "Previous" }))}</span>
        </button>
        {visiblePaginationPages.map((item, idx) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="services-catalog-page-ellipsis px-1" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`services-catalog-page-num doc-categories-pagination-num${page === item ? " is-active" : ""}`}
              disabled={busy}
              onClick={() => onPageChange(item)}
              aria-current={page === item ? "page" : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="services-catalog-page-pill doc-categories-pagination-pill"
          disabled={!hasCatalogPages || page >= totalPages || busy}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          <span>{String(t("common.next", { defaultValue: "Next" }))}</span>
          <MdChevronRight size={18} aria-hidden />
        </button>
      </nav>
      <span className="doc-categories-pagination-meta text-muted small">
        {t("document_categories.pageIndicator", {
          page,
          totalPages,
          defaultValue: `Page ${page} / ${totalPages}`,
        })}
      </span>
    </div>
  );
};
